/*! dm-variant-sync.js — Theme
 * Sync de preço/modal PIX/linha de parcelas sem polling contínuo.
 * Eventos: variant:change, shopify:variant:change, variant:update e aliases.
 */
(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const hostOf = (node) => (node && node.closest && node.closest('.shopify-section, dialog')) || document;

  const parseJSON = (value, fallback = {}) => {
    try {
      return JSON.parse(value || '');
    } catch (_error) {
      return fallback;
    }
  };

  const parsePct = (value) => {
    const parsed = parseFloat(String(value ?? '0').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const currencyFormatter = (localeMaybe, currencyMaybe) => {
    let locale = localeMaybe || navigator.language || 'pt-BR';
    const currency = currencyMaybe || 'BRL';

    if (/^[A-Z]{2}$/.test(locale)) {
      locale = locale === 'BR' ? 'pt-BR' : `${locale.toLowerCase()}-${locale}`;
    }

    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      });
      return (cents) => formatter.format((cents || 0) / 100);
    } catch (_error) {
      return (cents) => ((cents || 0) / 100).toFixed(2);
    }
  };

  const getVariantFromEvent = (event) => {
    const detail = (event && event.detail) || {};

    if (detail.variant) return detail.variant;
    if (detail.product && detail.product.selectedVariant) return detail.product.selectedVariant;

    const data = detail.data || {};
    if (data.variant) return data.variant;
    if (data.newProduct && data.newProduct.selectedVariant) return data.newProduct.selectedVariant;

    return null;
  };

  const inferInlineFlags = (inlineEl) => {
    if (!inlineEl || inlineEl.dataset.dmInlineFrozen) return;

    const source = (inlineEl.textContent || '').toLowerCase().normalize('NFD').replace(/\s+/g, ' ').trim();
    const hasAte = /(^|\s)até(\s|$)/i.test(inlineEl.textContent || '');
    const parcelMatch = source.match(/(\d+)\s*x/);
    const parcelCount = parcelMatch ? parseInt(parcelMatch[1], 10) : null;
    const isNoInterest = source.includes('sem juros');

    if (parcelCount) inlineEl.dataset.dmInlineN = String(parcelCount);
    if (isNoInterest) inlineEl.dataset.dmInlineNoInt = '1';
    if (hasAte) inlineEl.dataset.dmInlineHasAte = '1';

    inlineEl.dataset.dmInlineFrozen = '1';
  };

  const inlineConfig = (inlineEl, host) => {
    let insAccepted = parseInt(inlineEl?.dataset?.insAccepted || '0', 10);
    let insFree = parseInt(inlineEl?.dataset?.insFree || '0', 10);
    const insFormat = inlineEl?.dataset?.insFormat || '';

    const modal = host.querySelector('dialog-component.payment-method-modal');

    if ((!insAccepted || insAccepted < 1) && modal) {
      insAccepted = parseInt(modal.dataset.insMax || '12', 10);
    }

    if (insFree < 0 && modal) {
      insFree = parseInt(modal.dataset.insFree || '0', 10);
    }

    if (!Number.isFinite(insAccepted) || insAccepted < 1) insAccepted = 1;
    if (!Number.isFinite(insFree) || insFree < 0) insFree = 0;
    if (insFree > insAccepted) insFree = insAccepted;

    return {
      insAccepted,
      insFree,
      insFormat,
      interestMap: parseJSON(
        inlineEl?.dataset?.interestmap,
        parseJSON(modal?.dataset?.interestmap, {})
      ),
      locale: inlineEl?.dataset?.locale || modal?.dataset?.locale || navigator.language || 'pt-BR',
      currency: inlineEl?.dataset?.currency || modal?.dataset?.currency || 'BRL',
    };
  };

  const updatePaymentModal = (modalEl, priceCents) => {
    if (!modalEl || typeof priceCents !== 'number') return;

    let insMax = parseInt(modalEl.dataset.insMax || '12', 10);
    let insFree = parseInt(modalEl.dataset.insFree || '0', 10);

    if (!Number.isFinite(insMax) || insMax < 1) insMax = 12;
    if (!Number.isFinite(insFree) || insFree < 0) insFree = 0;
    if (insFree > insMax) insFree = insMax;

    const interestMap = parseJSON(modalEl.dataset.interestmap, {});
    const pixPct = parsePct(modalEl.dataset.pixPct || '0');
    const currency = modalEl.dataset.currency || 'BRL';
    const locale = modalEl.dataset.locale || navigator.language || 'pt-BR';
    const money = currencyFormatter(locale, currency);

    const tbody = modalEl.querySelector('.installments-table tbody');
    const pixAmount = modalEl.querySelector('.pix-amount');
    const boletoAmount = modalEl.querySelector('.boleto-amount');

    if (tbody) {
      for (let n = 1; n <= insMax; n += 1) {
        const row = tbody.querySelector(`tr[data-n="${n}"]`);
        if (!row) continue;

        let pct = parsePct(interestMap[n]);
        if (n <= insFree) pct = 0;

        const financed = priceCents + Math.round((priceCents * pct) / 100);
        const installment = Math.round(financed / n);

        const parcela = row.querySelector('.parcela');
        const total = row.querySelector('.tot');

        if (parcela) {
          parcela.textContent = `${n}x ${money(installment)} ${n <= insFree ? 'sem juros' : ''}`;
        }

        if (total) {
          total.textContent = money(financed);
        }
      }
    }

    if (pixAmount) {
      const discount = Math.round((priceCents * pixPct) / 100);
      pixAmount.textContent = `${money(priceCents - discount)} à vista`;
    }

    if (boletoAmount) {
      boletoAmount.textContent = `${money(priceCents)} à vista`;
    }
  };

  const updatePixCard = (pixCardEl, priceCents) => {
    if (!pixCardEl || typeof priceCents !== 'number') return;

    const discountPct = parsePct(pixCardEl.dataset.discountPct || '0');
    const host = hostOf(pixCardEl);
    const modal = host.querySelector('dialog-component.payment-method-modal');
    const currency = pixCardEl.dataset.currency || modal?.dataset?.currency || 'BRL';
    const locale = pixCardEl.dataset.locale || modal?.dataset?.locale || navigator.language || 'pt-BR';
    const money = currencyFormatter(locale, currency);

    const priceEl = pixCardEl.querySelector('.pix-price');
    const saveEl = pixCardEl.querySelector('.pix-save');

    const discount = Math.round((priceCents * discountPct) / 100);
    const pixTotal = priceCents - discount;

    if (priceEl) priceEl.textContent = money(pixTotal);
    if (saveEl) saveEl.textContent = money(discount);
  };

  const updateInlineLine = (inlineEl, priceCents) => {
    if (!inlineEl || typeof priceCents !== 'number') return;

    inferInlineFlags(inlineEl);

    const host = hostOf(inlineEl);
    const cfg = inlineConfig(inlineEl, host);
    const money = currencyFormatter(cfg.locale, cfg.currency);

    let installmentCount = inlineEl.dataset.dmInlineN ? parseInt(inlineEl.dataset.dmInlineN, 10) : null;

    if (!Number.isFinite(installmentCount) || installmentCount < 1) {
      installmentCount =
        cfg.insFormat === 'max_interest_free'
          ? cfg.insFree > 0
            ? cfg.insFree
            : 1
          : cfg.insAccepted > 0
            ? cfg.insAccepted
            : 1;
    }

    const noInterest = inlineEl.dataset.dmInlineNoInt === '1';
    let pct = noInterest ? 0 : parsePct(cfg.interestMap[installmentCount]);

    const financed = priceCents + Math.round((priceCents * pct) / 100);
    const installment = Math.round(financed / installmentCount);

    const prefix = inlineEl.dataset.dmInlineHasAte === '1' ? 'Até ' : '';
    const suffix = noInterest ? ' sem juros' : '';

    inlineEl.textContent = `${prefix}${installmentCount}x ${money(installment)}${suffix}`;
  };

  const updateInlineTargets = (host, priceCents) => {
    ['#pv_installments_value', '.dm-insline', '[id^="dm-insline-"]']
      .flatMap((selector) => $$(selector, host))
      .forEach((inlineEl) => updateInlineLine(inlineEl, priceCents));
  };

  const applyVariantToHost = (host, variant) => {
    if (!host || !variant) return;

    const cents = typeof variant.price === 'number' ? variant.price : parseInt(String(variant.price || 0), 10);
    if (!Number.isFinite(cents)) return;

    $$('.payment-method-modal', host).forEach((modalEl) => {
      updatePaymentModal(modalEl, cents);

      const hiddenVariantInput = modalEl.querySelector('form.payment-terms input[name="id"]');
      if (hiddenVariantInput && String(hiddenVariantInput.value) !== String(variant.id)) {
        hiddenVariantInput.value = String(variant.id);
        hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    $$('.pix-card[id^="pix-"]', host).forEach((pixCardEl) => updatePixCard(pixCardEl, cents));
    updateInlineTargets(host, cents);
  };

  const applyVariantIdToHost = (host, variantId) => {
    if (!host || !variantId) return;

    let cents = null;

    const modalEl = $('.payment-method-modal', host);
    if (modalEl) {
      const priceMap = parseJSON(modalEl.dataset.pricemap, {});
      if (typeof priceMap[variantId] === 'number') {
        cents = priceMap[variantId];
      }
    }

    if (cents == null) {
      const inlineEl =
        $('#pv_installments_value', host) ||
        $('.dm-insline', host) ||
        $('[id^="dm-insline-"]', host);

      if (inlineEl) {
        const inlinePriceMap = parseJSON(inlineEl.dataset.pricemap, {});
        if (typeof inlinePriceMap[variantId] === 'number') {
          cents = inlinePriceMap[variantId];
        }
      }
    }

    if (cents == null) {
      const pixCardEl = $('.pix-card[id^="pix-"]', host);
      if (pixCardEl) {
        const variantPrices = parseJSON(pixCardEl.dataset.variantPrices, {});
        if (typeof variantPrices[variantId] === 'number') {
          cents = variantPrices[variantId];
        }
      }
    }

    if (cents == null) return;

    $$('.payment-method-modal', host).forEach((modal) => {
      updatePaymentModal(modal, cents);
      const hiddenVariantInput = modal.querySelector('form.payment-terms input[name="id"]');

      if (hiddenVariantInput && String(hiddenVariantInput.value) !== String(variantId)) {
        hiddenVariantInput.value = String(variantId);
        hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    $$('.pix-card[id^="pix-"]', host).forEach((pixCardEl) => updatePixCard(pixCardEl, cents));
    updateInlineTargets(host, cents);
  };

  const bindVariantInputWatcher = (host) => {
    if (!host || host.dataset.dmVariantWatcherBound === '1') return;

    let variantInput = $('input[name="id"]', host);
    if (!variantInput) return;

    host.dataset.dmVariantWatcherBound = '1';

    let lastValue = String(variantInput.value || '');

    const syncVariant = () => {
      if (!host.isConnected) return;

      const currentInput = $('input[name="id"]', host);
      if (!currentInput) return;

      if (currentInput !== variantInput) {
        variantInput = currentInput;
        variantInput.addEventListener('change', syncVariant);
        variantInput.addEventListener('input', syncVariant);
      }

      const nextValue = String(variantInput.value || '');
      if (nextValue && nextValue !== lastValue) {
        lastValue = nextValue;
        applyVariantIdToHost(host, nextValue);
      }
    };

    variantInput.addEventListener('change', syncVariant);
    variantInput.addEventListener('input', syncVariant);

    const observer = new MutationObserver(syncVariant);
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value'],
    });

    if (lastValue) {
      applyVariantIdToHost(host, lastValue);
    }
  };

  const bindGlobal = () => {
    if (document.documentElement.dataset.dmSyncBound === '1') return;
    document.documentElement.dataset.dmSyncBound = '1';

    const syncFromEvent = (event) => {
      const host = hostOf(event.target instanceof Node ? event.target : null);
      const variant = getVariantFromEvent(event);

      if (variant) {
        applyVariantToHost(host, variant);
        return;
      }

      const fallbackId = $('input[name="id"]', host)?.value;
      if (fallbackId) {
        applyVariantIdToHost(host, fallbackId);
      }
    };

    const themeVariantEvent = (window.ThemeEvents && window.ThemeEvents.variantUpdate) || 'variant:update';

    ['variant:change', themeVariantEvent, 'shopify:variant:change', 'theme:variant:update', 'product:variant-update']
      .forEach((eventName) => {
        document.addEventListener(eventName, syncFromEvent, true);
      });

    const bootstrap = (scope = document) => {
      $$('.shopify-section, dialog', scope).forEach(bindVariantInputWatcher);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => bootstrap());
    } else {
      bootstrap();
    }

    document.addEventListener('shopify:section:load', (event) => bootstrap(event.target || document));
    document.addEventListener('shopify:section:reorder', (event) => bootstrap(event.target || document));

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches('.shopify-section, dialog')) {
            bindVariantInputWatcher(node);
            return;
          }

          if (node.querySelector) {
            const host = node.closest('.shopify-section, dialog') || node;
            bindVariantInputWatcher(host);
          }
        });
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  bindGlobal();
})();
